/**
 * reactionsContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import * as React from "react";

import AddReactionIcon from "@mui/icons-material/AddReaction";
import { Tooltip } from "@mui/material";

import { Membership } from "../models/auth";
import { ContentMessage } from "../models/message";
import { Reaction, ReactionContentType } from "../models/reaction";

interface Props {
  message: ContentMessage,
  myUserID: string,
  forceShow?: boolean,
  toggleReaction(msg: ContentMessage, reaction: Reaction): void,
  showEmojiPicker(anchor: HTMLElement, msg: ContentMessage): void
}
export function ReactionsContainer(props: Props): React.ReactElement<any, any> {
  const anchorRef = React.useRef<HTMLDivElement>(null);

  if (!props.forceShow && !props.message.reactions) { return <></>; }
  const keys = props.message.reactions ? Object.keys(props.message.reactions) : [];
  if (!props.forceShow && keys.length <= 0) { return <></>; }

  return <div className="reactions-container">
    {keys.map(key => {
      const reaction = (props.message.reactions as {[id: string]: Reaction})[key];
      if (!reaction.users || reaction.users.length <= 0) { return <></>; }
      const hasMe = reaction.users.findIndex(u => u.userID === props.myUserID) >= 0;
      return <div key={reaction._id} className={"reaction" + (hasMe ? " me" : "")}
          onClick={() => props.toggleReaction(props.message, reaction)}>
        <Tooltip arrow title={<ReactionUsers users={reaction.users} />}>
          <div>
            {reaction.contenttype === ReactionContentType.unicode ?
              <span className="reaction-content">{reaction.content}</span> :
              <img className="reaction-content" src={reaction.content} alt={":" + reaction.contentID + ":"} />}
            <span className="reaction-count">{reaction.users.length}</span>
          </div>
        </Tooltip>
      </div>;
    })}
    <div className="add-reaction" ref={anchorRef} aria-label="Add Reaction"
        onClick={() => anchorRef.current && props.showEmojiPicker(anchorRef.current, props.message)}>
      <AddReactionIcon />
    </div>
  </div>;
}

interface UsersProps {
  users: Array<{userID: string,
    firstname: string,
    lastname: string,
    membership?: Membership | null}>
}
function ReactionUsers(props: UsersProps): React.ReactElement<any, any> {
  const truncated = props.users.slice(0, 8);
  return <>{truncated.map((user, i) => {
    if (i > 0) { return <span key={user.userID} data-user-id={user.userID}>, {user.firstname} {user.lastname}</span>; }
    return <span key={user.userID} data-user-id={user.userID}>{user.firstname} {user.lastname}</span>
  })}
  {props.users.length > 8 && <>, and {props.users.length - 8} more</>}
  </>;
}
